import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ThemeToggle } from './ThemeToggle';
import { TenantSelector } from './TenantSelector';
import { SidebarMenu } from './SidebarMenu';
import { ThinNavRail } from './ThinNavRail';
import { navModeFromPreferences, nextNavMode } from './nav-sidebar-mode';
import { UserMenu } from './UserMenu';
// EventsMenu was a legacy hardcoded notifications bell linking to /notifications.
// The real Teams notifications bell is implemented in NotificationsBell.
// import { EventsMenu } from './EventsMenu';
import { NotificationsBell } from './NotificationsBell';
import { ServerError } from '../ServerError';
import { cn } from '../../lib/utils';
import { useActivityHeartbeat } from '../../hooks/useActivityHeartbeat';
import { apiClient } from '../../lib/api-client';
import { useCacheEvents } from '../../hooks/useCacheEvents';
import { UpdateRequiredOverlay } from '../UpdateRequiredOverlay';
import { hardReloadWithCacheBust } from '../UpdateRequiredOverlay';

const routeToName: Record<string, string> = {
  '/': 'Dashboard',
  '/dashboard': 'Dashboard',
  '/instances': 'Cloud Servers',
  '/clusters': 'OpenShift Clusters',
  '/openshift-clusters': 'OpenShift Clusters',
  '/cluster-brokers': 'Cluster Brokers',
  '/kubernetes-clusters': 'Kubernetes Clusters',
  '/mas-instances': 'MAS Instances',
  '/service-health': 'Service Health',
  '/monitoring': 'Monitoring',
  '/alerts': 'Alerts and Alarms',
  '/workflows': 'Workflows',
  '/cicd': 'CI/CD Flows',
  '/dev-instances': 'Container Instances',
  '/pr-instances': 'PR Instances',
  '/dev-images': 'Dev Images',
  '/approvals': 'Approvals',
  '/tenants': 'Tenants',
  '/user-groups': 'User Groups',
  '/users': 'Users',
  '/notifications': 'Notifications',
  '/convos': 'Convos',
  '/my-documents': 'My Documents',
  '/admin/cache': 'Cache Management',
  '/admin/users': 'Users',
  '/admin/groups': 'Groups',
  '/admin/approvals': 'Approvals Configuration',
  '/admin/ai-config': 'AI Workspaces',
  '/ai-workflows': 'AI Workflows',
  '/ai-learning': 'AI Learning',
};

function layoutPageTitle(pathname: string): string {
  const aiWs = pathname.match(/^\/admin\/ai-config\/([^/]+)\/?$/);
  if (aiWs) {
    const id = decodeURIComponent(aiWs[1]);
    return `AI Workspaces / ${id}`;
  }
  return routeToName[pathname] || 'Dashboard';
}

const routeToPage: Array<{ prefix: string; page: string }> = [
  { prefix: '/dashboard', page: 'dashboard' },
  { prefix: '/instances', page: 'instances' },
  { prefix: '/cluster-brokers', page: 'openshift_clusters' },
  { prefix: '/openshift-clusters', page: 'openshift_clusters' },
  { prefix: '/kubernetes-clusters', page: 'openshift_clusters' },
  { prefix: '/mas-instances', page: 'mas_instances' },
  { prefix: '/service-health', page: 'service_health' },
  { prefix: '/alerts', page: 'alerts' },
  { prefix: '/service-reports', page: 'service_reports' },
  { prefix: '/workflows', page: 'workflows' },
  { prefix: '/cicd', page: 'cicd' },
  { prefix: '/dev-instances', page: 'dev_instances' },
  { prefix: '/pr-instances', page: 'pr_instances' },
  { prefix: '/dev-images', page: 'dev_images' },
  { prefix: '/approvals', page: 'approvals' },
  { prefix: '/tenants', page: 'tenants' },
  { prefix: '/user-groups', page: 'user_groups' },
  { prefix: '/users', page: 'users' },
  { prefix: '/admin/cache', page: 'cache_management' },
  { prefix: '/admin/ai-config', page: 'ai_config' },
  { prefix: '/monitoring', page: 'monitoring' },
  { prefix: '/notifications', page: 'notifications' },
  { prefix: '/admin/approvals', page: 'approvals' },
  { prefix: '/', page: 'dashboard' },
];

function pageForPath(pathname: string): string | null {
  const hit = routeToPage.find(r => pathname === r.prefix || pathname.startsWith(`${r.prefix}/`));
  return hit?.page || null;
}

function safeReload(reason: string): void {
  // Prevent infinite reload loops if some background signal keeps firing (e.g. cache events flapping).
  // Session storage survives reloads, so this remains effective across refresh cycles.
  try {
    const key = 'portal:reload:last';
    const now = Date.now();
    const last = Number(window.sessionStorage.getItem(key) || '0') || 0;
    if (now - last < 15_000) {
      // Skip repeated reloads within 15s.
      return;
    }
    window.sessionStorage.setItem(key, String(now));
    window.sessionStorage.setItem('portal:reload:reason', reason);
  } catch {
    // ignore
  }
  window.location.reload();
}

export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const currentPageName = layoutPageTitle(location.pathname);
  
  let authResult;
  try {
    authResult = useAuth();
  } catch (err: any) {
    throw err;
  }
  const { user, isLoading, error, accessibleTenants, updatePreferences } = authResult;
  const [hasConnectionIssue, setHasConnectionIssue] = useState(false);
  const [updateRequired, setUpdateRequired] = useState(false);
  const [deployId, setDeployId] = useState<string | null>(null);
  const [nextDeployId, setNextDeployId] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [peekNavOpen, setPeekNavOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Tenants cache is used across almost the entire app (except Cache Management)
  const isTenantsCacheSubscriberRoute =
    !location.pathname.startsWith('/admin/cache');
  useActivityHeartbeat('cache:tenants', !!user && isTenantsCacheSubscriberRoute);

  // Meta-data cache (master.yaml) is subscribed by all users everywhere
  useActivityHeartbeat('cache:metadata', !!user);

  // Page-scoped monitoring caches (polling refresh increases only while users are on these pages)
  const currentPage = pageForPath(location.pathname);
  const isMonitoringCacheSubscriber =
    currentPage === 'alerts' || currentPage === 'service_health' || currentPage === 'dashboard';
  // The monitoring caches (jira/uptime/alertmanager/synthetic) use relatedPages like cache:service_health and cache:alerts.
  // Dashboard needs to behave like Service Health for refresh purposes, so we emit the same heartbeat key.
  const monitoringHeartbeatKey =
    currentPage === 'dashboard' ? 'cache:service_health' : `cache:${currentPage || 'unknown'}`;
  useActivityHeartbeat(
    monitoringHeartbeatKey,
    !!user && isMonitoringCacheSubscriber
  );

  // Targeted RBAC refresh: only affected users reload when RBAC YAML changes.
  useCacheEvents({
    enabled: !!user,
    cacheNames: ['rbac'],
    onEvent: (e) => {
      const email = String(user?.email || '').toLowerCase();
      const affected = (e.affectedUsers || []).map(u => String(u).toLowerCase());
      const isAffected = !!email && affected.includes(email);

      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[rbac event]', { cacheName: e.cacheName, version: e.version, affectedUsers: e.affectedUsers });
      }

      // Fast path: backend told us this user is affected.
      if (isAffected) {
        void (async () => {
          const currentPath = window.location.pathname;
          const currentPage = pageForPath(currentPath);

          try {
            const latest = await apiClient.getMe();
            const latestUser = latest?.user;
            if (!latestUser) {
              window.location.href = '/';
              return;
            }

            // Minimal local permission check: if current page is no longer allowed, go to root.
            const latestPerms = (latestUser as any)?.permissions || {};
            const canReadCurrent =
              !currentPage ||
              !!(latestPerms?.[currentPage]?.read ||
                latestPerms?.all_pages?.read ||
                latestPerms?.all_pages_all_options?.read);

            if (!canReadCurrent) {
              window.location.href = '/';
              return;
            }

            safeReload('rbac:affected');
          } catch {
            safeReload('rbac:affected:catch');
          }
        })();
        return;
      }

      // Safety net (covers cases where affectedUsers isn't present or is incomplete):
      // refetch /me and refresh auth only if this user's effective auth payload changed.
      // This stays targeted (unaffected users won't be refetched).
      if (!email) return;
      void (async () => {
        try {
          const latest = await apiClient.getMe();
          const latestUser = latest?.user;
          if (!latestUser) return;

          const currentFingerprint = JSON.stringify({
            email: String(user?.email || '').toLowerCase(),
            groups: (user?.groups || []).slice().sort(),
            tenant_access: (user as any)?.tenant_access || [],
            flags: (user as any)?.flags || {},
            page_flags: (user as any)?.page_flags || {},
            permissions: (user as any)?.permissions || {},
            accessibleTenants: (accessibleTenants || []).slice().sort(),
          });

          const latestFingerprint = JSON.stringify({
            email: String(latestUser.email || '').toLowerCase(),
            groups: (latestUser.groups || []).slice().sort(),
            tenant_access: (latestUser as any)?.tenant_access || [],
            flags: (latestUser as any)?.flags || {},
            page_flags: (latestUser as any)?.page_flags || {},
            permissions: (latestUser as any)?.permissions || {},
            accessibleTenants: (latest?.accessibleTenants || []).slice().sort(),
          });

          if (currentFingerprint !== latestFingerprint) {
            safeReload('rbac:fingerprint_changed');
          }
        } catch {
          // ignore
        }
      })();
    },
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Swipe gesture support for mobile
  useEffect(() => {
    if (!isMobile) return;

    const minSwipeDistance = 50; // minimum distance for a swipe
    let startX: number | null = null;
    let endX: number | null = null;

    const onTouchStart = (e: TouchEvent) => {
      setTouchEnd(null);
      startX = e.targetTouches[0].clientX;
      endX = null;
      setTouchStart(startX);
    };

    const onTouchMove = (e: TouchEvent) => {
      endX = e.targetTouches[0].clientX;
      setTouchEnd(endX);

      // Prevent browser back navigation on edge swipe; reserve that gesture for opening the menu.
      if (startX !== null && startX < 50 && endX - startX > 10) {
        e.preventDefault();
      }
    };

    const onTouchEnd = () => {
      const ts = startX ?? touchStart;
      const te = endX ?? touchEnd;
      if (!ts || !te) return;
      
      const distance = te - ts;
      const isLeftSwipe = distance < -minSwipeDistance;
      const isRightSwipe = distance > minSwipeDistance;
      
      // Open menu on right swipe from left edge (within 50px from left)
      if (isRightSwipe && ts < 50 && !isMobileMenuOpen) {
        setIsMobileMenuOpen(true);
      }
      
      // Close menu on left swipe when menu is open
      if (isLeftSwipe && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);

    return () => {
      document.removeEventListener('touchstart', onTouchStart as any);
      document.removeEventListener('touchmove', onTouchMove as any);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [isMobile, touchStart, touchEnd, isMobileMenuOpen]);

  const navMode = user ? navModeFromPreferences(user.preferences) : 'expanded';
  const navHidden = navMode === 'hidden';

  useEffect(() => {
    if (!navHidden) setPeekNavOpen(false);
  }, [navHidden]);

  useEffect(() => {
    const onConnectionError = () => setHasConnectionIssue(true);
    window.addEventListener('maxiron:server-connection-error', onConnectionError as EventListener);
    return () => window.removeEventListener('maxiron:server-connection-error', onConnectionError as EventListener);
  }, []);

  // Deploy-version detection:
  // Poll backend for current deployId. If it changes, show overlay prompting a hard reload.
  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      try {
        const res = await fetch(`/api/version?t=${Date.now()}`, {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!res.ok) return;
        const json = await res.json().catch(() => null);
        const next = String(json?.deployId || '').trim();
        if (!next) return;
        if (cancelled) return;

        const stored = localStorage.getItem('portal:deployId');
        if (!stored) {
          localStorage.setItem('portal:deployId', next);
          setDeployId(next);
          return;
        }

        setDeployId(stored);
        if (stored !== next) {
          setNextDeployId(next);
          setUpdateRequired(true);
        }
      } catch {
        // ignore
      }
    };

    void tick();
    const id = window.setInterval(() => void tick(), 60_000);
    const onFocus = () => void tick();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void tick();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Auto-recover: while backend is down, poll every 5s; once backend /api/health responds, hard-reload.
  useEffect(() => {
    if (!hasConnectionIssue) return;
    let cancelled = false;

    const tick = async () => {
      try {
        // Don't use apiClient here: /api/health may return non-JSON in some failure modes.
        // Must target backend (ingress routes /api -> backend). /health is routed to frontend and can be a false-positive 200.
        const res = await fetch(`/api/health?t=${Date.now()}`, {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        // Avoid false-positive recoveries when ingress returns an HTML page (frontend/index/maintenance) with 200.
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('text/html')) {
          throw new Error('HTML health response');
        }
        // If it is JSON, require the expected shape.
        if (ct.includes('application/json')) {
          const json = await res.json().catch(() => null);
          if (!json || json.status !== 'ok') {
            throw new Error('Bad health payload');
          }
        }
        if (cancelled) return;
        safeReload('server_recovered');
      } catch {
        // keep polling
      }
    };

    // quick first check, then interval
    void tick();
    const id = window.setInterval(() => void tick(), 5000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [hasConnectionIssue]);

  if (hasConnectionIssue) {
    return (
      <ServerError />
    );
  }

  // Handle errors first
  if (error) {
    // If error is about unauthorized access, redirect to login immediately
    if (error.includes('401') || error.includes('Unauthorized') || error.includes('Unauthorised')) {
      window.location.href = '/login';
      return null; // Return null while redirecting
    }
    
    // If error is about server connection (backend down), show friendly message
    if (error === 'SERVER_CONNECTION_ERROR' || error.includes('Failed to fetch') || error.includes('NetworkError')) {
      return <ServerError />;
    }
    
    // For other errors, display them
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-950 text-red-600 dark:text-red-400">
        Error: {error}
      </div>
    );
  }

  // If still loading or no user, DO NOT render children.
  // Rendering page components before auth is resolved causes brief "empty page" flashes.
  if (isLoading || !user) {
    return (
      <div className="flex min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-3 flex items-center justify-between sticky top-0 z-30 flex-shrink-0">
            <div className="flex items-center">
              <img 
                src="/icons/maxiron.png" 
                alt="Maxiron" 
                className="h-8 w-auto object-contain dark:hidden"
              />
              <img 
                src="/icons/maxiron_dark.png" 
                alt="Maxiron" 
                className="h-8 w-auto object-contain hidden dark:block"
              />
            </div>
          </header>
          <main
            className="flex-1 px-4 pb-4 pt-4 sm:px-6 sm:pb-6 sm:pt-4 lg:px-8 lg:pb-8 lg:pt-4 overflow-y-auto min-h-0 scrollbar-hidden"
            data-scroll-container
          >
            <div className="animate-pulse space-y-4 max-w-5xl">
              <div className="h-6 w-48 bg-gray-200 dark:bg-gray-800 rounded" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="h-28 bg-gray-200 dark:bg-gray-800 rounded" />
                <div className="h-28 bg-gray-200 dark:bg-gray-800 rounded" />
                <div className="h-28 bg-gray-200 dark:bg-gray-800 rounded" />
              </div>
              <div className="h-64 bg-gray-200 dark:bg-gray-800 rounded" />
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Only show simplified layout if user exists AND has no groups
  // Don't show for users who don't exist in the portal (user will be null)
  const hasNoGroups = user && (!user.groups || user.groups.length === 0);

  // If user exists but has no groups, show simplified layout
  if (hasNoGroups) {
    return (
      <div className="flex min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
        {/* Main Content Area - Full Width */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top Bar - Simplified */}
          <header className="h-14 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-3 flex items-center justify-between sticky top-0 z-30 flex-shrink-0">
            <div className="flex items-center">
              <img 
                src="/icons/maxiron.png" 
                alt="Maxiron" 
                className="h-8 w-auto object-contain dark:hidden"
              />
              <img 
                src="/icons/maxiron_dark.png" 
                alt="Maxiron" 
                className="h-8 w-auto object-contain hidden dark:block"
              />
            </div>

            <div className="flex items-center space-x-2">
              <ThemeToggle />
              <UserMenu />
            </div>
          </header>

          {/* Main Content */}
          <main
            className="flex-1 px-4 pb-4 pt-4 sm:px-6 sm:pb-6 sm:pt-4 lg:px-8 lg:pb-8 lg:pt-4 overflow-y-auto min-h-0 scrollbar-hidden"
            data-scroll-container
          >
            {children}
          </main>
        </div>
      </div>
    );
  }

  const sidebarChrome = (showMobileClose: boolean) => (
    <>
      <div className="h-14 px-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center">
          <img
            src="/icons/maxiron.png"
            alt="Maxiron"
            className="h-8 w-auto object-contain dark:hidden"
          />
          <img
            src="/icons/maxiron_dark.png"
            alt="Maxiron"
            className="h-8 w-auto object-contain hidden dark:block"
          />
        </div>
        {showMobileClose && (
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            title="Close Menu"
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 min-h-0 scrollbar-hidden" data-scroll-container>
        <SidebarMenu
          onItemClick={() => {
            if (isMobile) setIsMobileMenuOpen(false);
            if (!isMobile && navHidden) setPeekNavOpen(false);
          }}
        />
      </div>
    </>
  );

  // Normal layout for users with groups
  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      {updateRequired && (
        <UpdateRequiredOverlay
          message="We’ve updated Maxiron Portal. Please reload your browser to continue."
          onReload={() => {
            if (nextDeployId) {
              localStorage.setItem('portal:deployId', nextDeployId);
            }
            hardReloadWithCacheBust();
          }}
        />
      )}
      {/* Mobile Menu Overlay */}
      {isMobile && isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      {isMobile && (
        <aside
          className={cn(
            'w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col transition-transform duration-300 ease-in-out z-50',
            'fixed inset-y-0 left-0 transform',
            !isMobileMenuOpen && '-translate-x-full',
            isMobileMenuOpen && 'translate-x-0'
          )}
        >
          {sidebarChrome(true)}
        </aside>
      )}

      {/* Desktop: full sidebar */}
      {!isMobile && navMode === 'expanded' && (
        <aside className="fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
          {sidebarChrome(false)}
        </aside>
      )}

      {/* Desktop: icon rail */}
      {!isMobile && navMode === 'thin' && <ThinNavRail />}

      {/* Desktop: hidden — narrow hit strip; hover expands while pointer is over strip + panel */}
      {!isMobile && navHidden && (
        <div
          className={cn(
            'fixed inset-y-0 left-0 z-50 flex flex-col overflow-hidden transition-[width] duration-200 ease-out',
            peekNavOpen
              ? 'w-64 border-r border-gray-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900'
              : 'w-3 min-w-[12px] bg-transparent'
          )}
          onMouseEnter={() => setPeekNavOpen(true)}
          onMouseLeave={() => setPeekNavOpen(false)}
        >
          {peekNavOpen ? sidebarChrome(false) : null}
        </div>
      )}

      {/* Main Content Area */}
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col',
          !isMobile && navMode === 'expanded' && 'ml-64',
          !isMobile && navMode === 'thin' && 'ml-14'
        )}
      >
        {/* Top Bar */}
        <header className="h-14 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-3 flex items-center justify-between sticky top-0 z-30 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => {
                if (isMobile) setIsMobileMenuOpen(true);
                else {
                  const next = nextNavMode(navMode);
                  void updatePreferences({
                    navSidebarMode: next,
                    navSidebarCollapsed: next === 'hidden',
                  });
                }
              }}
              title={
                isMobile
                  ? 'Open menu'
                  : navMode === 'expanded'
                    ? 'Icon sidebar'
                    : navMode === 'thin'
                      ? 'Hide sidebar'
                      : 'Expand sidebar'
              }
              aria-expanded={isMobile ? isMobileMenuOpen : navMode !== 'hidden'}
              className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <Menu className="h-5 w-5" />
            </button>
            <h1 className="text-lg sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {currentPageName}
            </h1>
          </div>

          <div className="flex items-center space-x-2">
            <ThemeToggle />
            {authResult.accessibleTenants.length > 1 && <TenantSelector />}
            <NotificationsBell />
            {/* Legacy notifications bell removed (was hardcoded + navigated to /notifications). */}
            <UserMenu />
          </div>
        </header>

        {/* Main Content */}
        <main
          className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-4 pt-4 scrollbar-hidden sm:px-6 sm:pb-6 sm:pt-4 lg:px-8 lg:pb-8 lg:pt-4"
          data-scroll-container
        >
          {children}
        </main>
      </div>
    </div>
  );
}
